import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";

const AuthContext = createContext(null);

/**
 * Iter74.2 — Storage keys cleared on every logout.
 *
 * `USER_SCOPED_STORAGE_KEYS` are keys whose value belongs to the
 * currently-logged-in user and MUST NOT be visible to the next
 * login (e.g., read-notification IDs, command palette recents,
 * per-user filter last-seen state). Leaving any of these in place
 * would let identity B inherit identity A's private UI cache.
 *
 * `LEGACY_AUTH_TOKEN_KEYS` covers hypothetical token storage keys
 * that predate the current cookie-only auth. The current app does
 * NOT store JWTs client-side, but keeping this sweep in place means
 * any accidental future re-introduction (or third-party library
 * that squirrels a token away under a common name) can't leak
 * across logout.
 *
 * `asl_theme` and `asl_cmdk_hint_shown` are INTENTIONALLY excluded —
 * they are pure UI-preference keys with no PII / permission
 * implications, and blanking them would degrade UX (theme reverts
 * to "system", onboarding hint re-appears).
 */
const USER_SCOPED_STORAGE_KEYS = [
    "recently_viewed",
    "notifications_read",
    "asl_finance_scope",
    "asl_notif_read",
    "asl_cmdk_recents",
    "attendance.live",
    "attendance.invalidated_at",
];
const LEGACY_AUTH_TOKEN_KEYS = [
    "asl_token", "asl_jwt", "asl_access_token",
    "auth_token", "access_token", "id_token", "refresh_token",
    "session", "session_id",
];

export function AuthProvider({ children }) {
    // null = checking, false = anonymous, object = authenticated
    const [user, setUser] = useState(null);
    const [error, setError] = useState("");

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            return data;
        } catch (_e) {
            setUser(false);
            return null;
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = useCallback(async (email, password, extra = {}) => {
        setError("");
        try {
            const { data } = await api.post("/auth/login", { email, password, ...extra });
            // Iter75 — Branching auth response:
            //   * `mfa_required=true`     → return the challenge token,
            //     DON'T mark the user as logged-in yet. Caller renders
            //     the MFA challenge form and re-invokes `verifyMfa()`.
            //   * `password_rotation_required` (HTTP 428 handled below).
            //   * everything else — a full user profile (legacy shape).
            if (data && data.mfa_required && data.mfa_challenge_token) {
                return { mfaRequired: true, challengeToken: data.mfa_challenge_token, email: data.email };
            }
            setUser(data);
            return data;
        } catch (e) {
            const status = e?.response?.status;
            const detail = e?.response?.data?.detail;
            // Iter75.4 — Surface captcha requirement to caller so the
            // login form can mount the widget on the next attempt.
            if (typeof detail === "object" && detail?.captcha_required) {
                const captchaErr = new Error(detail.error === "captcha_required" ? "Verification required — please complete the challenge." : "Incorrect email or password.");
                captchaErr.captchaRequired = true;
                setError(captchaErr.message);
                throw captchaErr;
            }
            // Iter75.3 — Lockout responses (HTTP 423).
            if (status === 423 && typeof detail === "object") {
                const secs = detail?.retry_after_seconds || 60;
                const mins = Math.max(1, Math.ceil(secs / 60));
                const lockErr = new Error(
                    `Too many failed attempts. Try again in ~${mins} minute${mins === 1 ? "" : "s"}.`,
                );
                lockErr.lockedOut = true;
                lockErr.retryAfter = secs;
                setError(lockErr.message);
                throw lockErr;
            }
            // Iter75.2 — Password rotation required (HTTP 428).
            if (status === 428 && typeof detail === "object" && detail?.error === "password_rotation_required") {
                const rotErr = new Error("Your password has expired. Please set a new one to continue.");
                rotErr.passwordRotationRequired = true;
                rotErr.userId = detail.user_id;
                setError(rotErr.message);
                throw rotErr;
            }
            const msg = formatApiError(e);
            setError(msg);
            throw new Error(msg);
        }
    }, []);

    // Iter75 — Exchange the `mfa_challenge_token` for a real session.
    // Iter81 — Accept a `trustDevice` flag to opt in to the 7-day
    // trusted-device cookie. Default False — never trust silently.
    const verifyMfa = useCallback(async (challengeToken, code, isBackup = false, trustDevice = false) => {
        setError("");
        try {
            const { data } = await api.post("/auth/mfa/verify-challenge", {
                challenge_token: challengeToken,
                code,
                is_backup: isBackup,
                trust_device: !!trustDevice,
            });
            setUser(data);
            return data;
        } catch (e) {
            const msg = formatApiError(e);
            setError(msg);
            throw new Error(msg);
        }
    }, []);

    /**
     * Iter74.2 — Hard logout with comprehensive client-side reset.
     *
     * Order matters. We hit the backend FIRST (so its cookie-delete
     * response reaches the browser before anything else). Then we
     * scrub client storage, THEN drop the React auth state. Doing
     * `setUser(false)` last guarantees any subscribed component that
     * re-renders as a result already sees empty storage.
     *
     * A network failure on `/api/auth/logout` (e.g., offline, or
     * cookie already expired server-side) MUST NOT block the client
     * teardown — the point of logout is to unbind the user's
     * identity from this browser regardless of network state.
     */
    const logout = useCallback(async (reason) => {
        try {
            await api.post("/auth/logout", null, { params: reason ? { reason } : {} });
        } catch (_e) {
            /* ignore — client teardown must proceed regardless */
        }

        try {
            // Explicit named keys — surviving code paths still rely on
            // these names being cleared to avoid stale UI hints.
            for (const k of USER_SCOPED_STORAGE_KEYS) {
                localStorage.removeItem(k);
            }
            // Legacy / defensive token sweep — no-ops today but
            // provides a firewall against silent regressions.
            for (const k of LEGACY_AUTH_TOKEN_KEYS) {
                localStorage.removeItem(k);
                sessionStorage.removeItem(k);
            }
            // Anything else namespaced under known user-scoped prefixes
            // gets swept too. Note: `asl_theme` and `asl_cmdk_hint_shown`
            // are excluded above by the prefix check because they carry
            // no per-user state.
            const KEEP = new Set(["asl_theme", "asl_cmdk_hint_shown"]);
            const KILL_PREFIXES = ["asl_", "attendance.", "payroll.", "leave.", "staff."];
            const remove = [];
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (!key || KEEP.has(key)) continue;
                if (KILL_PREFIXES.some((p) => key.startsWith(p))) {
                    remove.push(key);
                }
            }
            for (const k of remove) localStorage.removeItem(k);
            // Session storage is short-lived — wipe entirely.
            sessionStorage.clear();
        } catch (_e) {
            /* ignore — storage may be unavailable in private mode */
        }

        // Drop the in-memory identity LAST so any component that
        // rerenders on user=false already sees a clean storage.
        setUser(false);
    }, []);

    return (
        <AuthContext.Provider value={{ user, error, login, verifyMfa, logout, refresh }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
    return ctx;
}
